import React, { useCallback, useRef, useState } from 'react';
import { Loader2, Play, Plus } from 'lucide-react';
import { DslExpressionEditor } from './dslAgentTest/DslExpressionEditor';
import { DslTagBank } from './dslAgentTest/DslTagBank';
import { DslResultChart } from './dslAgentTest/DslResultChart';
import { DslSummaryStrip } from './dslAgentTest/DslSummaryStrip';
import {
  DEFAULT_LINE_STYLE,
  DEFAULT_LINE_WIDTH,
  pickTagColor,
} from './dslAgentTest/colorPalette';
import { runDslTags } from './dslAgentTest/tagExecutor';
import type { DslTag, TagExecState } from './dslAgentTest/types';
import { DslErrorPayload, generateDsl } from '../services/DslService';
import styles from './DslAgentTestPage.module.css';

const MAX_TAGS = 10;

const DUPLICATE_NOTICE = 'DSL already in bank — remove the existing tag first.';
const FULL_NOTICE = `Tag bank is full (${MAX_TAGS} max). Remove a tag to add another.`;

const newTagId = (): string => {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  return `tag_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
};

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  return fallback;
};

const formatExecuteError = (payload: DslErrorPayload): string => {
  const location = typeof payload.offset === 'number' ? ` at offset ${payload.offset}` : '';
  const hint = payload.hint ? `\nHint: ${payload.hint}` : '';
  return `[${payload.category} / ${payload.code}] ${payload.message}${location}${hint}`;
};

const DslAgentTestPage: React.FC = () => {
  const [request, setRequest] = useState<string>('');
  const [dsl, setDsl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const [tags, setTags] = useState<DslTag[]>([]);
  const [tagBankNotice, setTagBankNotice] = useState<string | null>(null);

  const [execState, setExecState] = useState<Record<string, TagExecState>>({});
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [resetToken, setResetToken] = useState<number>(0);
  const [hiddenTagIds, setHiddenTagIds] = useState<Set<string>>(() => new Set());

  const tagsRef = useRef<DslTag[]>(tags);
  tagsRef.current = tags;
  const runIdRef = useRef<number>(0);

  const addDslToBank = useCallback((raw: string): void => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setTags((prev) => {
      if (prev.length >= MAX_TAGS) {
        setTagBankNotice(FULL_NOTICE);
        return prev;
      }
      if (prev.some((t) => t.dsl === trimmed)) {
        setTagBankNotice(DUPLICATE_NOTICE);
        return prev;
      }
      setTagBankNotice(null);
      return [
        ...prev,
        {
          id: newTagId(),
          dsl: trimmed,
          color: pickTagColor(prev.length),
          lineStyle: DEFAULT_LINE_STYLE,
          lineWidth: DEFAULT_LINE_WIDTH,
        },
      ];
    });
  }, []);

  const handleGenerate = useCallback(async () => {
    const trimmed = request.trim();
    if (!trimmed || isGenerating) return;

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const generated = await generateDsl(trimmed);
      setDsl(generated);
      addDslToBank(generated);
    } catch (caught: unknown) {
      setGenerateError(toErrorMessage(caught, 'Failed to generate DSL expression.'));
    } finally {
      setIsGenerating(false);
    }
  }, [request, isGenerating, addDslToBank]);

  const handleAddDslToBank = useCallback(() => {
    addDslToBank(dsl);
  }, [addDslToBank, dsl]);

  const handleRemoveTag = useCallback((id: string) => {
    setTags((prev) => prev.filter((t) => t.id !== id));
    setExecState((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setHiddenTagIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setTagBankNotice(null);
  }, []);

  const handleClearAllTags = useCallback(() => {
    setTags([]);
    setExecState({});
    setHiddenTagIds(new Set());
    setTagBankNotice(null);
  }, []);

  const handleToggleTagVisibility = useCallback((id: string) => {
    setHiddenTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const submittedTags = tagsRef.current;
    if (submittedTags.length === 0 || isExecuting) return;

    runIdRef.current += 1;
    const runId = runIdRef.current;
    const snapshot = [...submittedTags];

    setIsExecuting(true);
    setExecState((prev) => {
      const next = { ...prev };
      for (const tag of snapshot) {
        next[tag.id] = { status: 'loading', result: null, error: null };
      }
      return next;
    });

    try {
      const results = await runDslTags(snapshot);
      if (runIdRef.current !== runId) return;

      const stillPresent = new Set(tagsRef.current.map((t) => t.id));
      const updates: Record<string, TagExecState> = {};
      for (const tag of snapshot) {
        if (!stillPresent.has(tag.id)) continue;
        const entry = results.get(tag.id);
        if (!entry) continue;
        if (entry.kind === 'error') {
          updates[tag.id] = { status: 'error', result: null, error: entry.message };
        } else if (entry.response.error) {
          updates[tag.id] = {
            status: 'error',
            result: null,
            error: formatExecuteError(entry.response.error),
          };
        } else {
          updates[tag.id] = {
            status: 'ok',
            result: entry.response.result,
            error: null,
          };
        }
      }

      setExecState((prev) => ({ ...prev, ...updates }));
      setResetToken((n) => n + 1);
    } finally {
      if (runIdRef.current === runId) {
        setIsExecuting(false);
      }
    }
  }, [isExecuting]);

  const canSubmit = tags.length > 0 && !isExecuting;
  const canAddDslToBank = dsl.trim().length > 0;

  return (
    <main className={styles.page}>
      <section
        className={styles.panel}
        data-testid="dsl-agent-test-panel"
        aria-busy={isExecuting || isGenerating}
      >
        <h1 className={styles.title}>DSL Agent Test</h1>
        <p className={styles.subTitle}>
          Describe the analytics you want, generate DSL expressions, add them to the tag bank, then submit to execute every tag concurrently.
        </p>

        <DslExpressionEditor
          request={request}
          onRequestChange={setRequest}
          dsl={dsl}
          onDslChange={setDsl}
          isGenerating={isGenerating}
          generateError={generateError}
          onGenerate={handleGenerate}
        />

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleAddDslToBank}
            disabled={!canAddDslToBank}
            aria-label="Add current DSL expression to the tag bank"
            data-testid="dsl-agent-add-to-bank-button"
          >
            <Plus size={16} aria-hidden="true" />
            <span>Add DSL to bank</span>
          </button>
        </div>

        <DslTagBank
          tags={tags}
          execStateById={execState}
          onRemoveTag={handleRemoveTag}
          onClearAll={handleClearAllTags}
          maxTags={MAX_TAGS}
        />

        {tagBankNotice ? (
          <p className={styles.noticeBlock} data-testid="dsl-agent-tag-notice" role="status">
            {tagBankNotice}
          </p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="Execute every DSL tag"
            data-testid="dsl-agent-submit-button"
          >
            {isExecuting ? (
              <Loader2 size={16} className={styles.spin} aria-hidden="true" />
            ) : (
              <Play size={16} aria-hidden="true" />
            )}
            <span>{isExecuting ? 'Executing…' : 'Submit'}</span>
          </button>
        </div>

        <DslResultChart
          tags={tags}
          execState={execState}
          resetToken={resetToken}
          hiddenTagIds={hiddenTagIds}
        />

        <DslSummaryStrip
          tags={tags}
          execState={execState}
          hiddenTagIds={hiddenTagIds}
          onToggleVisibility={handleToggleTagVisibility}
        />
      </section>
    </main>
  );
};

export default DslAgentTestPage;
